'use client';

import {
    Alert,
    AlertBody,
    Button,
    Checkbox,
    Drawer,
    Heading,
    Icon,
    LayoutLinelengthContainer,
    List,
    LoadingIndicator,
    TextLink,
    TextPassage,
} from '@petsmart-ui/sparky';
import cn from 'classnames';
import { type MouseEvent, useEffect, useId, useMemo, useState } from 'react';

import { useStore, useStoreDispatch } from '@libs/common-next';
import type { SearchStore, StoreSearchParams } from '@libs/common-types';
import { getLocale, NAVIGATION_URLS, stringifyStoreId } from '@libs/core';

import { getAlertText, getPickedStore, getUserLocation, myStoreFirstSort, resolveStoreDrawerSearch } from './libs';
import { type StoreDrawerOptions, useStoreDrawerStore, useStoreSearchQuery } from './model';
import { StoreListItem } from './ui';

import './pdp-store-drawer.scss';

const allStoresURL = NAVIGATION_URLS.legacy.stores;

export type PDPStoreDrawerProps = {
    showBackButton?: boolean;
    /** When the host drawer is closed, the stacked store drawer should close too. */
    forceClosed?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    /** Sparky close (X / overlay / Esc). Used when stacked over another drawer. */
    onCloseAll?: () => void;
    /**
     * Controlled open state. When omitted, the PDP zustand store (`useStoreDrawer`) is used.
     * MiniPdpDrawer passes this so its instance stays independent from the page-level drawer.
     */
    isActive?: boolean;
} & StoreDrawerOptions;

export function PDPStoreDrawer({
    showBackButton = false,
    forceClosed = false,
    onOpenChange,
    onCloseAll,
    isActive: isActiveProp,
    className: classNameProp,
    toggleText: toggleTextProp,
    drawerHeading: drawerHeadingProp,
    alertIcon: alertIconProp,
    alertHeading: alertHeadingProp,
    sku: skuProp,
    minPurchaseQuantity: minPurchaseQuantityProp,
    filterFn: filterFnProp,
}: PDPStoreDrawerProps) {
    const { isOpen: storeIsOpen, options: storeOptions, close } = useStoreDrawerStore();
    const { updateSelectedStore } = useStoreDispatch();
    const { selectedStore, selectedStoreId } = useStore();
    const headingId = useId();
    const isControlled = isActiveProp !== undefined;
    const options: StoreDrawerOptions = isControlled
        ? {
              className: classNameProp,
              toggleText: toggleTextProp,
              drawerHeading: drawerHeadingProp,
              alertIcon: alertIconProp,
              alertHeading: alertHeadingProp,
              sku: skuProp,
              minPurchaseQuantity: minPurchaseQuantityProp,
              filterFn: filterFnProp,
          }
        : storeOptions;
    const { sku, filterFn, minPurchaseQuantity = 0 } = options;
    const [pickedStore, setPickedStore] = useState<undefined | number>(undefined);
    const [showInventoryOnly, setShowInventoryOnly] = useState(false);
    const [searchOptions, setSearchOptions] = useState<StoreSearchParams>({});
    const [searchBoxText, setSearchBoxText] = useState('');
    // Skip Sparky first-open hack for stacked instances; MiniPdpDrawer already delays first render.
    const [firstTimeOpenHack, setFirstTimeOpenHack] = useState(() => !showBackButton);
    const { search: resolvedSearch, enabled: isStoreSearchEnabled } = resolveStoreDrawerSearch({
        searchOptions,
        store: selectedStore.data,
        selectedStoreId,
        isSelectedStorePending: selectedStore.isPending,
    });
    const searchQuery = useStoreSearchQuery({
        ...resolvedSearch,
        sku,
        minimumCaseQty: minPurchaseQuantity,
        enabled: isStoreSearchEnabled,
    });

    const storesWithInventory = useMemo(
        () =>
            showInventoryOnly
                ? searchQuery.data.filter(s => Boolean(s.Store.HasCurrentProductInStock))
                : searchQuery.data,
        [searchQuery.data, showInventoryOnly]
    );

    const storesToShow = useMemo(
        () => (typeof filterFn === 'function' ? filterFn(storesWithInventory) : storesWithInventory),
        [storesWithInventory, filterFn]
    );
    myStoreFirstSort(storesToShow, selectedStore.data?.StoreNumber);

    useEffect(() => {
        if (searchQuery.data.length > 0) {
            if (sku) {
                const atLeastOneInStock = searchQuery.data.some(s => s.Store.HasCurrentProductInStock);
                setShowInventoryOnly(atLeastOneInStock);
            } else {
                setShowInventoryOnly(false);
            }
        }
    }, [searchQuery.data, sku]);

    useEffect(() => {
        if (!searchQuery.isLoading) {
            const newPickedStore = getPickedStore(storesToShow, pickedStore, selectedStore.data?.StoreNumber);
            setPickedStore(newPickedStore);
        }
    }, [storesToShow, searchQuery.isLoading]);

    function closeTopDrawer(result: string | null = null) {
        if (isControlled) {
            onOpenChange?.(false);
            return;
        }
        close(result);
        onOpenChange?.(false);
    }

    function handleSelect() {
        const storeId = stringifyStoreId(pickedStore) ?? null;
        if (isControlled && storeId) {
            updateSelectedStore(storeId);
        }
        closeTopDrawer(storeId);
    }

    function handleBack(event: MouseEvent) {
        event.stopPropagation();
        event.preventDefault();
        closeTopDrawer();
    }

    function handleClose() {
        closeTopDrawer();
        onCloseAll?.();
    }

    useEffect(() => {
        if (forceClosed) {
            closeTopDrawer();
        }
    }, [forceClosed]);

    useEffect(() => {
        if (selectedStore.data) {
            setPickedStore(selectedStore.data.StoreNumber);
        }
    }, [selectedStore.data?.StoreNumber]);

    async function handleUseMyLocation() {
        const { isGeolocationEnabled, latitude: lat, longitude: lng } = await getUserLocation();
        setSearchBoxText('');

        if (!isGeolocationEnabled) {
            setSearchOptions({});
            return;
        }

        setSearchOptions({ lat, lng });
    }

    function handleSearch() {
        setSearchOptions({ query: searchBoxText });
    }

    function handleToggleHasItems() {
        setShowInventoryOnly(!showInventoryOnly);
    }

    function handleSearchBoxChange(value: string) {
        setSearchBoxText(value);
    }

    function handleStoreClick(item: SearchStore) {
        setPickedStore(item.Store.StoreNumber);
    }

    useEffect(() => {
        setFirstTimeOpenHack(false);
    }, []);

    const openState = isControlled ? Boolean(isActiveProp) : storeIsOpen;
    const isActive = firstTimeOpenHack ? true : openState;
    const isConfirmButtonDisabled = searchQuery.isLoading || searchQuery.data.length < 1 || !pickedStore;
    const isConfirmButtonVisible = Boolean(pickedStore);
    const showAlert = !searchQuery.isLoading && storesToShow.length === 0;

    const alertContent = getAlertText(searchBoxText);
    const drawerHeading = options.drawerHeading ?? 'Find a store';
    const placeholder = getLocale() === 'en-CA' ? 'City or postal code' : 'City, state or zip code';
    const showList = storesToShow.length > 0;
    const isLoading = searchQuery.isLoading;

    return (
        <Drawer
            align="right" // Has to be hardcoded, sparky doesn't work well when align is dynmically passed
            className={cn(
                'c-store-search-drawer',
                'store-drawer',
                'pdp-store-drawer',
                options.className || 'pdp-page',
                {
                    'store-drawer-ftoh': firstTimeOpenHack,
                    'pdp-store-drawer--stacked': showBackButton,
                }
            )}
            isActive={isActive}
            onClose={handleClose}
            dismissible
            ariaLabelledBy={headingId}
        >
            <Drawer.Header className="pdp-store-drawer__header">
                {showBackButton && (
                    <button
                        type="button"
                        aria-label="Back"
                        className="pdp-store-drawer__back"
                        data-testid="pdp-store-drawer-back-btn"
                        onClick={handleBack}
                    >
                        <Icon name="chevron-left" size="lg" aria-hidden="true" />
                    </button>
                )}
                <Heading id={headingId} tagName="h2" size="headline">
                    {drawerHeading}
                </Heading>
            </Drawer.Header>
            <Drawer.Body className="c-store-search-drawer__body">
                <div className="c-store-search-drawer__search">
                    <div className="c-store-search-drawer__search-input-container">
                        <input
                            type="text"
                            className="c-store-search-drawer__input"
                            placeholder={placeholder}
                            value={searchBoxText}
                            onChange={event => handleSearchBoxChange(event.target.value)}
                            onKeyDown={event => {
                                if (event.key === 'Enter') {
                                    handleSearch();
                                }
                            }}
                        />
                        <Button
                            className="c-store-search-drawer__search-button"
                            variant="link"
                            iconPosition="before"
                            iconName="search"
                            text="Search"
                            hideText
                            onClick={handleSearch}
                        />
                        {searchBoxText ? (
                            <Button
                                className="c-store-search-drawer__clear-button"
                                variant="link"
                                iconPosition="before"
                                iconName="x"
                                text="Clear"
                                hideText
                                onClick={() => handleSearchBoxChange('')}
                            />
                        ) : null}
                    </div>
                    <Button
                        className="c-store-search-drawer__use-location"
                        variant="link"
                        text="Use my current location"
                        iconPosition="before"
                        iconName="nearme-fill"
                        onClick={handleUseMyLocation}
                    />
                </div>
                {options.toggleText ? (
                    <div className="sparky-c-list__item c-store-search-drawer__listing sparky-c-with-availability">
                        <TextPassage>{options.toggleText}</TextPassage>
                        <Checkbox
                            toggle
                            onChange={handleToggleHasItems}
                            checked={showInventoryOnly}
                            key={`inventory-checkbox${showInventoryOnly ? '--checked' : ''}`}
                        />
                    </div>
                ) : null}
                {showAlert ? (
                    <Alert
                        variant="error"
                        iconName={options.alertIcon ?? 'exclamation-circle-fill'}
                        className="c-store-search-drawer__alert"
                    >
                        <AlertBody>
                            <LayoutLinelengthContainer>
                                <Heading tagName="h2" size="body-bold">
                                    {alertContent?.title ?? 'No locations found'}
                                </Heading>
                            </LayoutLinelengthContainer>
                            <TextPassage>
                                <p>
                                    {alertContent?.text ? (
                                        <>
                                            {alertContent.text}
                                            {alertContent.link ? (
                                                <a href={alertContent.link} target="_blank" rel="noreferrer">
                                                    {alertContent.linkText}
                                                </a>
                                            ) : null}
                                        </>
                                    ) : null}
                                </p>
                            </TextPassage>
                        </AlertBody>
                    </Alert>
                ) : null}
                <div className="c-store-search-drawer__listing">
                    {showList && !isLoading ? (
                        <List data-testid="store-drawer-list">
                            {storesToShow.map(store => (
                                <StoreListItem
                                    key={store.Store.StoreNumber}
                                    item={store}
                                    onClick={() => handleStoreClick(store)}
                                    showStock={true}
                                    isSelected={pickedStore === store.Store.StoreNumber}
                                    isMyStore={store.Store.StoreNumber === selectedStore.data?.StoreNumber}
                                />
                            ))}
                        </List>
                    ) : null}
                    {!showList && isLoading ? (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                textAlign: 'center',
                            }}
                        >
                            <LoadingIndicator />
                        </div>
                    ) : null}
                    {!showList && !isLoading ? (
                        <TextPassage style={{ textAlign: 'center' }}>
                            {showAlert && allStoresURL ? (
                                <TextLink href={allStoresURL} className="c-store-search-drawer__all-stores-button">
                                    View all stores
                                </TextLink>
                            ) : (
                                'No results'
                            )}
                        </TextPassage>
                    ) : null}
                </div>
            </Drawer.Body>
            {isConfirmButtonVisible ? (
                <Drawer.Footer>
                    <Button
                        onClick={handleSelect}
                        variant="primary"
                        text="Select store"
                        fullWidth
                        disabled={isConfirmButtonDisabled}
                    />
                </Drawer.Footer>
            ) : null}
        </Drawer>
    );
}
